import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRemoteAttendanceEntry } from './hooks/useRemoteAttendanceEntry';
import AppDropdown from '../../../components/AppDropdown';
import AppCombobox from '../../../components/AppCombobox';
import AppDateInput from '../../../components/AppDateInput';
import { fetchEmployeeOptions } from '../../../api/filterOptionApi';
import { appToast } from '../../../components/AppToast';
import {
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
  AMPM_OPTIONS,
} from './api/remoteAttendanceApi';

export default function RemoteAttendanceEntryScreen() {
  const { state, actions } = useRemoteAttendanceEntry();
  const navigate = useNavigate();
  const { form, isEdit, isLoading, isSaving, reasonOptions } = state;
  const isApproved = isEdit && form.status === 'Approved';

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center p-16">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        <p className="mt-3 text-xs font-semibold text-on-surface-variant">Loading Remote Attendance Details...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Remote Attendance' : 'Remote Attendance Entry'}
          </h1>
        </div>

        <Link
          to="/payroll/remote-attandance"
          className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface-variant hover:text-primary hover:bg-surface-container-low transition shadow-xs w-fit"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Form Card */}
        <div className="lg:col-span-8 rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm overflow-hidden flex flex-col justify-between">
          {/* Card Header */}
          <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-8 py-4">
            <h2 className="font-display text-lg font-bold text-on-surface">Remote Attendance</h2>
          </div>

          {/* Form Body */}
          <div className="p-8">
            <div className="max-w-xl mx-auto space-y-4">
              {isApproved && (
                <div className="flex items-center gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs font-bold text-emerald-700 dark:text-emerald-300">
                  <span className="material-symbols-outlined text-lg">verified</span>
                  <span>This remote attendance record has already been approved and is locked for editing.</span>
                </div>
              )}

              {/* Employee Combobox */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Employee <span className="text-error">*</span>
                </label>
                <div className="flex items-center gap-1.5 w-full">
                  <div className="flex-1 min-w-0">
                    <AppCombobox
                      fetchOptions={fetchEmployeeOptions}
                      value={form.emp_id ? Number(form.emp_id) : ''}
                      initialLabel={form.emp_name || form.employee || ''}
                      disabled={isApproved}
                      onChange={(val, opt) => {
                        actions.patchForm('emp_id', val ? String(val) : '');
                        if (opt?.label) {
                          actions.patchForm('emp_name', opt.label);
                          actions.patchForm('employee', opt.label);
                        }
                      }}
                      placeholder="Select Employee..."
                      searchPlaceholder="Search employee name or code..."
                      triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                    />
                  </div>
                  <button
                    type="button"
                    title="Employee info"
                    onClick={() => appToast.info('Select the employee marking remote attendance.')}
                    className="h-9 w-9 flex items-center justify-center rounded-lg border border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:text-primary transition shrink-0 cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-base">info</span>
                  </button>
                  <button
                    type="button"
                    title="Create new employee"
                    onClick={() => navigate('/masters/acheads/heads/entry?group=Employees')}
                    className="h-9 w-9 flex items-center justify-center rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/20 text-primary transition shrink-0 font-bold cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-base">add</span>
                  </button>
                </div>
              </div>

              {/* Date */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Date <span className="text-error">*</span>
                </label>
                <div className="max-w-xs">
                  <AppDateInput
                    value={form.date}
                    disabled={isApproved}
                    onChange={(val) => actions.patchForm('date', val)}
                    inputClassName="h-9 text-xs"
                  />
                </div>
              </div>

              {/* Time (Hour : Minute : AM/PM) */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Time <span className="text-error">*</span>
                </label>
                <div className="flex items-center gap-2 max-w-xs">
                  <div className="flex-1">
                    <AppDropdown
                      options={HOUR_OPTIONS}
                      value={form.hour}
                      disabled={isApproved}
                      onChange={(val) => actions.patchForm('hour', val)}
                      triggerClassName="h-9 bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-2 text-center"
                    />
                  </div>
                  <span className="text-xs font-bold text-on-surface-variant">:</span>
                  <div className="flex-1">
                    <AppDropdown
                      options={MINUTE_OPTIONS}
                      value={form.minute}
                      disabled={isApproved}
                      onChange={(val) => actions.patchForm('minute', val)}
                      triggerClassName="h-9 bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-2 text-center"
                    />
                  </div>
                  <div className="w-20">
                    <AppDropdown
                      options={AMPM_OPTIONS}
                      value={form.ampm}
                      disabled={isApproved}
                      onChange={(val) => actions.patchForm('ampm', val)}
                      triggerClassName="h-9 bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-2 text-center font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Reason <span className="text-error">*</span>
                </label>
                <AppDropdown
                  options={reasonOptions}
                  value={String(form.reason_id || '')}
                  disabled={isApproved}
                  onChange={(val) => actions.patchForm('reason_id', val)}
                  placeholder="Select Reason"
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Remarks */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-start gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right pt-2">
                  Remarks {String(form.reason_id) === '5' && <span className="text-error">*</span>}
                </label>
                <textarea
                  rows={4}
                  value={form.remarks}
                  disabled={isApproved}
                  onChange={(e) => actions.patchForm('remarks', e.target.value)}
                  placeholder={String(form.reason_id) === '5' ? 'Detailed explanation is required for Other reason...' : 'Mention reason for remote attendance (e.g. Work from home, client visit, field work)...'}
                  className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-low p-3 text-xs text-on-surface outline-none placeholder:text-on-surface-variant focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-y disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* GPS In Section */}
          <div className="border-t border-outline-variant/20 bg-surface-container-low/30 px-8 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-on-surface">GPS In</span>
              <span className="text-[11px] text-on-surface-variant font-medium">
                {form.gpsLocation
                  ? `(${form.gpsLocation})`
                  : '(Remote Check-in)'}
              </span>
            </div>
            <button
              type="button"
              title="Detect current location"
              disabled={isApproved}
              onClick={() => {
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      const coords = `${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`;
                      actions.patchForm('gpsLocation', coords);
                      appToast.success('Location captured: ' + coords);
                    },
                    () => {
                      appToast.info('Location access unavailable. Defaulting to Remote Check-in.');
                    },
                    { timeout: 5000 }
                  );
                } else {
                  appToast.info('Geolocation not supported by your browser.');
                }
              }}
              className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-base">my_location</span>
              <span>Detect Location</span>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-8 py-4">
            <button
              type="button"
              onClick={() => appToast.info('Audit history tracking is active for Remote Attendance.')}
              className="inline-flex items-center gap-1.5 rounded-xl bg-surface-container-low px-4 py-2 text-xs font-bold text-on-surface-variant transition hover:bg-surface-container-high"
            >
              <span className="material-symbols-outlined text-sm">history</span>
              <span>History</span>
            </button>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={isSaving || isApproved}
                onClick={() => actions.handleSave({ exitAfter: true })}
                className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-sm">save</span>
                <span>{isSaving ? 'Saving...' : 'save & eXit'}</span>
              </button>

              {!isEdit && (
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => actions.handleSave({ exitAfter: false })}
                  className="h-9 px-5 bg-surface-container-lowest hover:bg-surface-container-low text-primary font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/40 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Save &amp; continue
                </button>
              )}

              {form.status === 'Pending' && (
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={actions.handleApproveAndExit}
                  className="h-9 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="material-symbols-outlined text-sm">check_circle</span>
                  <span>Approve &amp; Exit</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => navigate('/payroll/remote-attandance')}
                className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        {/* Right Info Panel */}
        <div className="lg:col-span-4 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-on-surface">
            <span className="material-symbols-outlined text-base text-amber-500 font-bold">lightbulb</span>
            <span>TIPS:</span>
          </div>

          <p className="text-xs text-on-surface-variant leading-relaxed font-medium">
            Use this screen to mark remote attendance when working off-site or on official outdoor assignments.
          </p>

          <div className="pt-3 border-t border-outline-variant/15 space-y-2">
            <h4 className="text-[11px] font-bold text-on-surface uppercase tracking-wider">INSTRUCTIONS:</h4>
            <ul className="text-xs text-on-surface-variant space-y-2 list-disc list-inside leading-relaxed">
              <li>Select the target employee from the organization directory.</li>
              <li>Specify the date and time for the remote attendance entry.</li>
              <li>Add detailed remarks specifying the exact reason (mandatory for reason &apos;Other&apos;).</li>
              <li>GPS location is automatically captured or can be refreshed via &quot;Detect Location&quot;.</li>
              <li>Attendance entries are subject to review and approval by your reporting manager.</li>
              <li>Approved entries are locked for editing.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

